<script setup lang="ts">
/**
 * 源站 ScratchCard 1:1：cover 涂层 + destination-out 圆刷 22 + 松手抽样 ≥45% complete。
 */
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    brushRadius?: number;
    coverUrl: string;
    disabled?: boolean;
    emptyText?: string;
    prizeUrl?: string;
    threshold?: number;
  }>(),
  {
    brushRadius: 22,
    disabled: false,
    emptyText: '?',
    prizeUrl: '',
    threshold: 0.45,
  },
);

const emit = defineEmits<{
  complete: [];
  ready: [{ height: number; width: number }];
}>();

const rootRef = ref<HTMLElement | null>(null);
const canvasRef = ref<HTMLCanvasElement | null>(null);
const canvasVisible = ref(true);
const scratching = ref(false);
const completed = ref(false);

let ctx: CanvasRenderingContext2D | null = null;
let dpr = 1;
let cssWidth = 0;
let cssHeight = 0;
let raf = 0;
let paintingCover = false;
let coverDrawn = false;
let resizeObserver: ResizeObserver | null = null;
const imageCache = new Map<string, HTMLImageElement>();

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve, reject) => {
    if (!url) {
      resolve(null);
      return;
    }
    const cached = imageCache.get(url);
    if (cached) {
      if (cached.complete) {
        resolve(cached);
        return;
      }
      cached.addEventListener('load', () => resolve(cached), { once: true });
      return;
    }
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      imageCache.set(url, image);
      resolve(image);
    };
    image.onerror = reject;
    image.src = url;
  });
}

async function paintCover() {
  const canvas = canvasRef.value;
  if (!canvas || !ctx || cssWidth < 2 || cssHeight < 2 || paintingCover) {
    return;
  }
  paintingCover = true;
  try {
    const image = await loadImage(props.coverUrl);
    if (!canvas || !ctx) {
      return;
    }
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, cssWidth, cssHeight);
    if (image) {
      ctx.drawImage(image, 0, 0, cssWidth, cssHeight);
    } else {
      ctx.fillStyle = '#c80f2e';
      ctx.fillRect(0, 0, cssWidth, cssHeight);
    }
    coverDrawn = true;
    canvasVisible.value = true;
    completed.value = false;
    emit('ready', { width: cssWidth, height: cssHeight });
  } catch {
    if (!ctx) {
      return;
    }
    ctx.fillStyle = '#c80f2e';
    ctx.fillRect(0, 0, cssWidth, cssHeight);
    coverDrawn = true;
    canvasVisible.value = true;
    completed.value = false;
  } finally {
    paintingCover = false;
  }
}

function measure() {
  const root = rootRef.value;
  if (!root) {
    return { height: 0, width: 0 };
  }
  const box = root.getBoundingClientRect();
  return {
    height: Math.max(0, Math.round(box.height)),
    width: Math.max(0, Math.round(box.width)),
  };
}

async function resizeCanvas() {
  const root = rootRef.value;
  const canvas = canvasRef.value;
  if (!root || !canvas) {
    return;
  }
  const { width, height } = measure();
  if (width < 2 || height < 2 || (width === cssWidth && height === cssHeight && ctx && coverDrawn)) {
    return;
  }
  dpr = window.devicePixelRatio || 1;
  cssWidth = width;
  cssHeight = height;
  coverDrawn = false;
  canvas.width = Math.floor(cssWidth * dpr);
  canvas.height = Math.floor(cssHeight * dpr);
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  ctx = canvas.getContext('2d');
  ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
  await paintCover();
}

function scheduleResize() {
  window.cancelAnimationFrame(raf);
  raf = window.requestAnimationFrame(() => {
    void resizeCanvas();
  });
}

function posFromEvent(event: { clientX: number; clientY: number }) {
  const canvas = canvasRef.value;
  if (!canvas) {
    return null;
  }
  const box = canvas.getBoundingClientRect();
  return { x: event.clientX - box.left, y: event.clientY - box.top };
}

function eraseAt(x: number, y: number) {
  if (!ctx || props.disabled || completed.value) {
    return;
  }
  ctx.globalCompositeOperation = 'destination-out';
  ctx.beginPath();
  ctx.arc(x, y, props.brushRadius, 0, Math.PI * 2);
  ctx.fill();
}

function clearedRatio() {
  const canvas = canvasRef.value;
  if (!canvas || !ctx) {
    return 0;
  }
  const { width, height } = canvas;
  const data = ctx.getImageData(0, 0, width, height).data;
  let cleared = 0;
  let samples = 0;
  const step = 4 * Math.max(1, Math.floor(dpr));
  for (let i = 3; i < data.length; i += step) {
    samples += 1;
    if (data[i] === 0) {
      cleared += 1;
    }
  }
  return samples ? cleared / samples : 0;
}

function maybeComplete() {
  if (completed.value) {
    return;
  }
  if (clearedRatio() >= props.threshold) {
    completed.value = true;
    canvasVisible.value = false;
    emit('complete');
  }
}

function onMouseDown(event: MouseEvent) {
  if (props.disabled || completed.value) {
    return;
  }
  scratching.value = true;
  const pos = posFromEvent(event);
  if (pos) {
    eraseAt(pos.x, pos.y);
  }
}

function onMouseMove(event: MouseEvent) {
  if (!scratching.value || props.disabled || completed.value) {
    return;
  }
  const pos = posFromEvent(event);
  if (pos) {
    eraseAt(pos.x, pos.y);
  }
}

function onMouseUp() {
  if (!scratching.value) {
    return;
  }
  scratching.value = false;
  maybeComplete();
}

function onTouchStart(event: TouchEvent) {
  if (props.disabled || completed.value) {
    return;
  }
  event.stopPropagation();
  scratching.value = true;
  const touch = event.touches[0];
  if (!touch) {
    return;
  }
  const pos = posFromEvent(touch);
  if (pos) {
    eraseAt(pos.x, pos.y);
  }
}

function onTouchMove(event: TouchEvent) {
  if (props.disabled || completed.value) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  if (!scratching.value) {
    return;
  }
  const touch = event.touches[0];
  if (!touch) {
    return;
  }
  const pos = posFromEvent(touch);
  if (pos) {
    eraseAt(pos.x, pos.y);
  }
}

function onTouchEnd(event: TouchEvent) {
  event.stopPropagation();
  onMouseUp();
}

async function reset() {
  completed.value = false;
  canvasVisible.value = true;
  coverDrawn = false;
  await nextTick();
  scheduleResize();
}

onMounted(async () => {
  await nextTick();
  if (typeof ResizeObserver !== 'undefined' && rootRef.value) {
    resizeObserver = new ResizeObserver(() => {
      scheduleResize();
    });
    resizeObserver.observe(rootRef.value);
  } else {
    window.addEventListener('resize', scheduleResize);
  }
  scheduleResize();
});

onBeforeUnmount(() => {
  window.cancelAnimationFrame(raf);
  resizeObserver?.disconnect();
  window.removeEventListener('resize', scheduleResize);
});

watch(
  () => props.coverUrl,
  () => {
    coverDrawn = false;
    scheduleResize();
  },
);

defineExpose({ reset, resizeCanvas });
</script>

<template>
  <div ref="rootRef" class="scratch-card" :class="{ 'scratch-card--disabled': disabled }">
    <div class="scratch-card__prize">
      <img v-if="prizeUrl" class="scratch-card__prize-img" :src="prizeUrl" alt="" />
      <div v-else class="scratch-card__prize-empty">{{ emptyText }}</div>
    </div>
    <canvas
      ref="canvasRef"
      class="scratch-card__canvas"
      :class="{ 'scratch-card__canvas--hidden': !canvasVisible }"
      @mousedown="onMouseDown"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
      @mouseleave="onMouseUp"
      @touchstart.prevent="onTouchStart"
      @touchmove.prevent="onTouchMove"
      @touchend.prevent="onTouchEnd"
      @touchcancel.prevent="onTouchEnd"
    />
  </div>
</template>

<style scoped>
.scratch-card {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 1.06667rem;
  touch-action: none;
}

.scratch-card__prize {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fb9308;
  border-radius: 1.06667rem;
}

.scratch-card__prize-img {
  width: 100%;
  max-width: 4rem;
  height: 100%;
  max-height: 4rem;
  object-fit: cover;
}

.scratch-card__prize-empty {
  color: #757577;
  font-size: 0.64rem;
  font-weight: 600;
}

.scratch-card__canvas {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  cursor: crosshair;
  touch-action: none;
}

.scratch-card__canvas--hidden {
  opacity: 0;
  pointer-events: none;
}

.scratch-card--disabled .scratch-card__canvas {
  cursor: not-allowed;
}
</style>
